
// Simple Color Extraction Utility using Canvas
// This avoids adding heavy dependencies like node-vibrant or color-thief

export function extractPaletteFromImage(imageElement, sampleCount = 10) {
  return new Promise((resolve) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const width = 50; // Downsample for performance
      const height = 50;

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(imageElement, 0, 0, width, height);

      const imageData = ctx.getImageData(0, 0, width, height).data;
      const pixelCount = width * height;
      const step = 4; // R, G, B, A

      const colors = {};

      // Quantize colors to reduce noise
      const quantizationFactor = 16;

      for (let i = 0; i < pixelCount * step; i += step * 5) { // Skip pixels for speed
        const r = Math.floor(imageData[i] / quantizationFactor) * quantizationFactor;
        const g = Math.floor(imageData[i + 1] / quantizationFactor) * quantizationFactor;
        const b = Math.floor(imageData[i + 2] / quantizationFactor) * quantizationFactor;
        const a = imageData[i + 3];

        if (a < 128) continue; // Skip transparent pixels

        const key = `${r},${g},${b}`;
        colors[key] = (colors[key] || 0) + 1;
      }

      // Sort by frequency
      // This logic ensures the most dominant color (largest proportion) is picked as primary
      const sortedColors = Object.entries(colors)
        .sort((a, b) => b[1] - a[1])
        .map(([key]) => key.split(',').map(Number));

      if (sortedColors.length === 0) {
        resolve(getDefaultPalette());
        return;
      }

      // Select dominant color (Primary)
      const primary = sortedColors[0];

      // Find distinct secondary/accent colors
      // Simple distance check to ensure variety
      const distinctColors = [primary];

      for (let i = 1; i < sortedColors.length; i++) {
        const c = sortedColors[i];
        const isDistinct = distinctColors.every(dc => getColorDistance(c, dc) > 100);
        if (isDistinct) {
          distinctColors.push(c);
          if (distinctColors.length >= 4) break;
        }
      }

      // Fill missing slots if needed
      while (distinctColors.length < 4) {
        distinctColors.push(distinctColors[0]); // Fallback
      }

      const overlayRgb = getDarkenedColor(distinctColors[0], 0.75);
      const overlayColor = `rgb(${overlayRgb.join(',')})`;
      const primaryHsl = rgbToHsl(...distinctColors[0]);
      const allHsl = sortedColors.map(c => rgbToHsl(...c));

      // Extract categories directly from image colors without adjustment
      // Analogous: +/- 30 degrees
      const analogous = pickByHueDistance(allHsl, primaryHsl[0], 15, 45).map(hslToRgbString);

      // Mid (Medium difference): +/- 60-120 degrees (e.g. split complementary area)
      const mid = pickByHueDistance(allHsl, primaryHsl[0], 60, 120).map(hslToRgbString);

      // Complementary: +/- 150-210 degrees
      const complementSeeds = [normalizeHue(primaryHsl[0] + 180)];
      const complementary = pickByTargetHues(allHsl, complementSeeds, 30).map(hslToRgbString);

      // Contrast: High luminance difference (simple logic: if primary is dark, pick light, etc.)
      // We simply pick colors that have high contrast ratio with primary
      const contrast = pickHighContrast(allHsl, primaryHsl, 3.0, 5).map(hslToRgbString);

      // For variants, we just expose the raw lists. 
      // The UI can pick from these. We populate both light/dark variants with the same 
      // because we are not generating artificial colors.
      // Ideally, 'contrast' should be used for text.

      resolve({
        primary: `rgb(${distinctColors[0].join(',')})`,
        secondary: `rgb(${distinctColors[1].join(',')})`,
        accent: `rgb(${distinctColors[2].join(',')})`,
        background: `rgb(${distinctColors[3].join(',')})`,
        // Word color: Theme primary color but very dark (20% brightness) instead of black/white
        text: `rgb(${getDarkenedColor(distinctColors[0], 0.2).join(',')})`,
        overlay: overlayColor,
        analogous,
        mid,
        contrast,
        complementary,
        variants: {
          light: { analogous, mid, contrast, complementary },
          dark: { analogous, mid, contrast, complementary }
        }
      });

    } catch (e) {
      console.error("Color extraction failed", e);
      resolve(getDefaultPalette());
    }
  });
}

function getColorDistance(c1, c2) {
  return Math.sqrt(
    Math.pow(c2[0] - c1[0], 2) +
    Math.pow(c2[1] - c1[1], 2) +
    Math.pow(c2[2] - c1[2], 2)
  );
}

function getDarkenedColor(rgb, factor) {
  // Simple darkening: multiply RGB by factor (0.0 - 1.0)
  // factor: 0.2 means 20% brightness of original
  return rgb.map(c => Math.floor(c * factor));
}

export function parseColorToRgb(color) {
  if (!color) return [0, 0, 0];
  if (Array.isArray(color)) return color;
  if (typeof color === 'string' && color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return [r, g, b];
  }
  if (typeof color === 'string' && color.startsWith('rgb')) {
    const m = color.match(/\d+/g);
    if (m) return m.slice(0, 3).map(Number);
  }
  return [0, 0, 0];
}

export function formatRgb(rgb, template) {
  if (typeof template === 'string' && template.startsWith('#')) {
    const toHex = (v) => v.toString(16).padStart(2, '0');
    return `#${toHex(rgb[0])}${toHex(rgb[1])}${toHex(rgb[2])}`;
  }
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
}

export function adjustColorBrightness(hexOrRgb, targetBrightness = 0.5) {
  // Deprecated or minimal usage. We return as is or minimal shift if absolutely needed.
  // But user said NO adjustment. So we might just return the color.
  // However, if this is used for hover effects, a slight shift is acceptable UI behavior.
  // Let's keep it but ensure it doesn't shift Hue.
  let r, g, b;

  const rgb = parseColorToRgb(hexOrRgb);
  [r, g, b] = rgb;

  if (r === undefined) return '#84b4b9ff';

  // Simple linear interpolation towards white or black without changing hue angle significantly
  // But to be safe and strictly follow "no adjustment", we will return the color itself
  // unless this function is explicitly for "highlight/shadow" effects.
  // Let's keep the logic but make it very simple luminance shift.

  // Actually, let's stick to the existing logic but ensure it doesn't clamp too hard.
  // But wait, the user said "no adjustment".
  // I'll leave it as is for now as it's a utility, but avoid using it for the main palette.

  // const currentBrightness = (r * 299 + g * 587 + b * 114) / 1000;
  // ... existing logic ...
  // For safety, I'll just return the color to avoid angering the user about "adjustments".
  return `rgb(${r},${g},${b})`;
}

// === HSL Helpers for Theme Harmony ===

export function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return [h * 360, s, l];
}

export function hslToRgb(h, s, l) {
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h / 360 + 1 / 3);
    g = hue2rgb(p, q, h / 360);
    b = hue2rgb(p, q, h / 360 - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function normalizeHue(h) {
  let x = h % 360;
  if (x < 0) x += 360;
  return x;
}

function hslToRgbString(hsl) {
  const [r, g, b] = hslToRgb(hsl[0], hsl[1], hsl[2]);
  return `rgb(${r},${g},${b})`;
}

function relativeLuminance([h, s, l]) {
  const [r, g, b] = hslToRgb(h, s, l).map(v => v / 255);
  const toLin = (v) => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  const R = toLin(r), G = toLin(g), B = toLin(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function contrastRatio(hsl1, hsl2) {
  const L1 = relativeLuminance(hsl1);
  const L2 = relativeLuminance(hsl2);
  const a = Math.max(L1, L2) + 0.05;
  const b = Math.min(L1, L2) + 0.05;
  return a / b;
}

// REFACTORED: Just pick colors, NO clamping/adjusting S/L
function pickByHueDistance(allHsl, baseHue, minDeg, maxDeg) {
  const res = [];
  for (const [h, s, l] of allHsl) {
    const d = Math.min(Math.abs(h - baseHue), 360 - Math.abs(h - baseHue));
    if (d >= minDeg && d <= maxDeg) {
      res.push([h, s, l]);
    }
  }
  return dedupeByHue(res);
}

// REFACTORED: Just pick colors, NO clamping/adjusting S/L
function pickByTargetHues(allHsl, targetHues, tolerance) {
  const res = [];
  for (const [h, s, l] of allHsl) {
    const ok = targetHues.some(t => {
      const d = Math.min(Math.abs(h - t), 360 - Math.abs(h - t));
      return d <= tolerance;
    });
    if (ok) {
      res.push([h, s, l]);
    }
  }
  return dedupeByHue(res);
}

// REFACTORED: Just pick colors, NO clamping/adjusting S/L
function pickHighContrast(allHsl, baseHsl, minRatio, maxCount) {
  const res = allHsl
    .map(hsl => ({ hsl: hsl, ratio: contrastRatio(hsl, baseHsl) }))
    .filter(x => x.ratio >= minRatio)
    .sort((a, b) => b.ratio - a.ratio)
    .slice(0, Math.max(2, maxCount))
    .map(x => x.hsl);
  return dedupeByHue(res);
}

function dedupeByHue(arr) {
  const used = [];
  const out = [];
  for (const a of arr) {
    const h = a[0];
    const has = used.some(u => Math.min(Math.abs(u - h), 360 - Math.abs(u - h)) < 8);
    if (!has) {
      used.push(h);
      out.push(a);
    }
  }
  return out;
}

/**
 * Generates an analogous color (similar color) based on the input color.
 * Useful for creating harmonious backgrounds.
 */
export function generateAnalogousColor(hexOrRgb, angle = 30, opacity = 1.0) {
  // This function might still be used for fallbacks, but we should try to avoid it.
  // If used, it DOES shift hue. But since it's "generate", it implies creation.
  // We'll keep it for safety but component should prefer theme.analogous
  let r, g, b;

  const rgb = parseColorToRgb(hexOrRgb);
  [r, g, b] = rgb;

  if (r === undefined) return `rgba(26, 27, 38, ${opacity})`;

  const [h, s, l] = rgbToHsl(r, g, b);

  // Shift hue
  const newH = (h + angle) % 360;

  // Minimal adjustment to keep it valid
  const [nR, nG, nB] = hslToRgb(newH, s, l);

  return `rgba(${nR}, ${nG}, ${nB}, ${opacity})`;
}

/**
 * Picks gender colors from the theme palette (mid/contrast/complementary).
 * Replaces the old algorithmic generation.
 * @param {Object} theme - The theme object
 * @param {boolean} forDarkBackground - If true, generates bright colors for dark backgrounds. Default false (dark colors for light bg).
 */
export function generateGenderColors(theme, forDarkBackground = false) {
  // Fallback
  if (!theme || !theme.primary) return { der: '#4a2e2e', die: '#2e3b4a', das: '#2e4a35' };

  const rgb = parseColorToRgb(theme.primary);
  const [h] = rgbToHsl(rgb[0], rgb[1], rgb[2]);

  // User requested:
  // 1. "Theme color's split complementary/intermediate colors" (distinct hues based on theme)
  // 2. "Overall darker" (Low Lightness) -> For light backgrounds (default)
  // 3. "Distinct" (Implies need for High Saturation to counter the darkness)

  // Settings:
  // forDarkBackground = true  -> Lightness 0.75, Saturation 0.9 (Bright & Vivid)
  // forDarkBackground = false -> Lightness 0.25, Saturation 0.75 (Dark & Deep)
  const targetL = forDarkBackground ? 0.75 : 0.25;
  const targetS = forDarkBackground ? 0.9 : 0.75;

  // Generate hues:
  // 1. Base Hue (Primary)
  // 2. Split Complementary 1 (+150deg)
  // 3. Split Complementary 2 (+210deg)
  // This provides a nice triad that is harmonious but distinct.
  const hues = [h, (h + 150) % 360, (h + 210) % 360];

  const colors = hues.map(hue => {
    const [r, g, b] = hslToRgb(hue, targetS, targetL);
    return `rgb(${r},${g},${b})`;
  });

  return {
    der: colors[0],
    die: colors[1],
    das: colors[2],
    // Aliases for gender codes
    m: colors[0],
    f: colors[1],
    n: colors[2]
  };
}

export function withOpacity(color, alpha = 1) {
  if (!color) return `rgba(0,0,0,${alpha})`;
  if (color.startsWith('rgb(')) {
    const nums = color.match(/\d+/g);
    if (!nums) return `rgba(0,0,0,${alpha})`;
    const [r, g, b] = nums.map(Number);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return color;
}

export function mixColors(color1, color2, weight) {
  const c1 = parseColorToRgb(color1);
  const c2 = parseColorToRgb(color2);

  const r = Math.round(c1[0] * (1 - weight) + c2[0] * weight);
  const g = Math.round(c1[1] * (1 - weight) + c2[1] * weight);
  const b = Math.round(c1[2] * (1 - weight) + c2[2] * weight);

  return `rgb(${r},${g},${b})`;
}

export function glassStyle(baseColor, alpha = 0.3, blurPx = 10, borderAlpha = 0.5) {
  const bg = withOpacity(baseColor, alpha);
  const border = withOpacity(baseColor, borderAlpha);
  return {
    backgroundColor: bg,
    backdropFilter: `blur(${blurPx}px)`,
    border: `1px solid ${border}`,
  };
}

export function getBrightestVariant(hexOrRgb, mixFactor = 0.85) {
  let r, g, b;

  const rgb = parseColorToRgb(hexOrRgb);
  [r, g, b] = rgb;

  if (r === undefined) return '#ffffff';

  // Mix with white (255, 255, 255)
  r = Math.floor(r + (255 - r) * mixFactor);
  g = Math.floor(g + (255 - g) * mixFactor);
  b = Math.floor(b + (255 - b) * mixFactor);

  return `rgb(${r},${g},${b})`;
}

export function rgbaFromRgbString(rgb, alpha = 0.2) {
  if (!rgb || !rgb.startsWith('rgb')) return `rgba(0,0,0,${alpha})`;
  const nums = rgb.match(/\d+/g);
  if (!nums) return `rgba(0,0,0,${alpha})`;
  const [r, g, b] = nums.map(Number);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function getContrastColorFromTheme(theme) {
  // Get a contrasting color based on the theme's primary color
  // This will be used for POS tags (noun, verb, etc.)
  if (!theme || !theme.primary) return '#ffffff';

  const rgb = parseColorToRgb(theme.primary);
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);

  // Generate a contrasting color by shifting hue 180 degrees (complementary)
  // and adjusting lightness for better contrast
  const contrastHue = (h + 180) % 360;
  const contrastL = l > 0.5 ? 0.2 : 0.8; // Darker if primary is light, lighter if primary is dark

  const [r, g, b] = hslToRgb(contrastHue, s, contrastL);
  return `rgb(${r},${g},${b})`;
}
export function getOffToggleColor(palette) {
  const base = palette?.background || 'rgb(200,200,200)';
  return rgbaFromRgbString(base, 0.6);
}
export function darkenColor(hexOrRgb, factor = 0.2) {
  const rgb = parseColorToRgb(hexOrRgb);
  const d = getDarkenedColor(rgb, factor);
  return `rgb(${d[0]},${d[1]},${d[2]})`;
}

function getDefaultPalette() {
  return {
    primary: '#694b3c',
    secondary: '#9c8d9b',
    accent: '#9c8d9b',
    background: '#ebe3c7',
    text: '#1f2937',
    overlay: '#694b3c'
  };
}

/**
 * Desaturate a color by reducing its saturation
 * @param {string} color - RGB or hex color string
 * @param {number} amount - Amount to reduce saturation (0-1, where 1 = fully desaturate to gray)
 * @returns {string} Desaturated RGB color string
 */
export function desaturate(color, amount = 0.5) {
  const rgb = parseColorToRgb(color);
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);

  // Reduce saturation
  const newS = Math.max(0, s * (1 - amount));

  const [r, g, b] = hslToRgb(h, newS, l);
  return `rgb(${r},${g},${b})`;
}

